import { Reveal } from "./Reveal";
import { ASSETS, IMAGES, scrollToId } from "@/lib/site";
import { ArrowUpRight } from "lucide-react";

const CARDS = [
    {
        title: "Brennholz",
        desc: "Buche, Eiche, Birke, Fichte oder Mischholz — gespalten auf 20–60 cm, waldfrisch oder abgelagert. Ab 50 € pro Schüttraummeter.",
        img: IMAGES.brennholz,
        span: "md:col-span-7",
        tag: "Ab 50 €/SRM",
        bright: false,
    },
    {
        title: "Bauholz",
        desc: "Bohlen, Balken und Kantholz aus heimischen Wäldern — sägerau und auf Maß zugeschnitten.",
        img: IMAGES.bauholz,
        span: "md:col-span-5",
        tag: "Auf Maß",
    },
    {
        title: "Forstdienstleistungen",
        desc: "Forstwirtschaftliche Arbeiten rund um Ihren Wald — individuell nach Absprache, direkt bei Ihnen vor Ort.",
        img: IMAGES.forst,
        span: "md:col-span-5",
        tag: "Vor Ort",
    },
    {
        title: "Holzkunst",
        desc: "Massivholz-Tische, River-Tables und Einzelstücke aus heimischem Holz — jedes Werk ein handgefertigtes Unikat.",
        img: ASSETS.kunst[5],
        span: "md:col-span-7",
        tag: "Unikate",
        bright: false,
        galleryKey: "holzkunst",
    },
];

export const Angebote = () => (
    <section id="angebote" className="mx-auto max-w-7xl px-6 py-24 lg:py-32" data-testid="angebote-section">
        <Reveal>
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#C88A45]">Was ich anbiete</p>
            <h2 className="mb-16 max-w-2xl font-display text-4xl font-extrabold uppercase tracking-tight text-[#EDE6DA] sm:text-5xl lg:text-6xl">
                Vom Wald direkt <span className="text-[#C88A45]">zu Ihnen.</span>
            </h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
            {CARDS.map((c, i) => (
                <Reveal key={c.title} delay={i * 0.1} className={c.span}>
                    <button
                        data-testid={`angebot-card-${c.title.toLowerCase()}`}
                        onClick={() => {
                            if (c.galleryKey) {
                                window.dispatchEvent(new CustomEvent("gallery:select", { detail: c.galleryKey }));
                                scrollToId("galerie");
                            } else {
                                scrollToId("kontakt");
                            }
                        }}
                        className="group block w-full overflow-hidden rounded border border-[#2E2517] bg-[#1B150E] text-left transition-colors duration-300 hover:border-[#C88A45]/40"
                    >
                        <div className="relative h-64 overflow-hidden sm:h-72">
                            <img
                                src={c.img}
                                alt={c.title}
                                className={`${c.bright ? "img-bright" : "img-warm"} h-full w-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-105`}
                            />
                            <span className="absolute left-4 top-4 rounded-full bg-[#120E09]/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#C88A45] backdrop-blur-sm">
                                {c.tag}
                            </span>
                        </div>
                        <div className="flex items-start justify-between gap-4 p-6">
                            <div>
                                <h3 className="font-display text-xl font-bold uppercase tracking-tight text-[#EDE6DA] [hyphens:auto] sm:text-2xl">{c.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-[#9C917D]">{c.desc}</p>
                            </div>
                            <ArrowUpRight
                                size={22}
                                className="mt-1 shrink-0 text-[#9C917D] transition-[color,transform] duration-300 group-hover:translate-x-1 group-hover:text-[#C88A45]"
                            />
                        </div>
                    </button>
                </Reveal>
            ))}
        </div>
        <Reveal delay={0.2}>
            <div
                data-testid="kuqs-certificate"
                className="mt-10 flex flex-col items-start gap-6 rounded border border-[#2E2517] bg-[#120E09] p-6 sm:flex-row sm:items-center sm:p-8"
            >
                <div className="shrink-0 rounded bg-white p-3">
                    <img
                        src="/kuqs-logo.jpeg"
                        alt="KUQS — Kompetenznachweis zu Qualitäts- und Sicherheitsstandards in forstwirtschaftlichen Dienstleistungsunternehmen"
                        className="h-28 w-auto sm:h-32"
                        data-testid="kuqs-logo"
                    />
                </div>
                <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C88A45]">Zertifizierter Betrieb</p>
                    <h3 className="mt-2 font-display text-lg font-bold uppercase tracking-tight text-[#EDE6DA] [hyphens:auto] break-words sm:text-2xl">
                        KUQS-zertifiziertes Forst&shy;dienstleistungs&shy;unternehmen
                    </h3>
                    <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#9C917D]">
                        KischioWood besitzt den KUQS-Kompetenznachweis zu Qualitäts- und Sicherheitsstandards in
                        forstwirtschaftlichen Dienstleistungsunternehmen — geprüfte Sicherheit und Qualität bei allen
                        Forstarbeiten. Sie finden uns in der offiziellen KUQS-Datenbank über unsere Registriernummer:
                    </p>
                    <p className="mt-3 text-sm text-[#EDE6DA]">
                        Registriernummer:{" "}
                        <a
                            href="http://sachsenforst.com/kuqs"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded border border-[#C88A45]/40 bg-[#1B150E] px-3 py-1 font-mono font-semibold tracking-wider text-[#C88A45] transition-colors duration-200 hover:border-[#C88A45] hover:bg-[#C88A45]/10"
                            data-testid="kuqs-regnummer"
                            title="In der KUQS-Datenbank im Suchfeld eingeben"
                        >
                            KUQS 574274
                        </a>
                    </p>
                    <p className="mt-2 text-xs text-[#9C917D]">
                        Öffnet die offizielle KUQS-Firmendatenbank — dort die Nummer einfach ins Suchfeld eingeben.
                    </p>
                </div>
            </div>
        </Reveal>
    </section>
);
